import { skipToken, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { SavingsRateChartData } from '../savings-rate-query/types'
import { SAVINGS_RATE_TIMEFRAMES, SavingsRateTimeframe } from './common'
import { SavingsRateQueryOptions } from '@/config/chain/types'
import { SimplifiedQueryResult } from '@/domain/common/query'
import { Token } from '@/domain/types/Token'
import { Timeframe } from '@/ui/charts/defaults'
import { filterDataByTimeframe } from '@/ui/charts/utils'

export interface UseSavingsRateInfoParams {
  savingsToken: Token
  currentTimestamp: number
  staleTime: number
  savingsRateQueryOptions: SavingsRateQueryOptions | undefined
  historyCutoff?: Date
  chainId: number
}

export interface UseSavingsRateInfoResult {
  queryResult: SimplifiedQueryResult<SavingsRateChartData>
  selectedTimeframe: SavingsRateTimeframe
  setSelectedTimeframe: (timeframe: Timeframe) => void
  availableTimeframes: SavingsRateTimeframe[]
}

export function useSavingsRateInfo({
  savingsToken,
  currentTimestamp,
  staleTime,
  savingsRateQueryOptions,
  historyCutoff,
  chainId,
}: UseSavingsRateInfoParams): UseSavingsRateInfoResult {
  const [selectedTimeframe, setSelectedTimeframe] = useState<SavingsRateTimeframe>('3M')

  const result = useQuery(
    savingsRateQueryOptions
      ? { ...savingsRateQueryOptions({ savingsToken, historyCutoff, chainId }), staleTime }
      : { queryKey: ['unsupported-savings-rate-query'], queryFn: skipToken },
  )

  const queryResult = useMemo(() => {
    if (!result.data) {
      return result
    }

    const filtered = filterDataByTimeframe({
      data: result.data.apy,
      timeframe: selectedTimeframe,
      currentTimestamp,
    })
    return {
      ...result,
      data: { apy: filtered },
    }
  }, [result, selectedTimeframe, currentTimestamp])

  return {
    queryResult,
    selectedTimeframe,
    setSelectedTimeframe: (selectedTimeframe) => {
      if (SAVINGS_RATE_TIMEFRAMES.includes(selectedTimeframe)) {
        setSelectedTimeframe(selectedTimeframe as any)
      }
    },
    availableTimeframes: SAVINGS_RATE_TIMEFRAMES,
  }
}
